import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { DatasetListParams, datasetApi } from '@/api/datasetApi'
import { getPageByUrl } from '@/api/vpApi'
import { Dataset, FileMetadata } from '@/types/dataset'
import { PaginatedResponse, WithListParams } from '@/types/pagination'
import { objectToQueryString } from '@/utils/url'

export const datasetQueryKeys = {
  all: ['datasets'] as const,
  list: (params?: DatasetListParams) =>
    [...datasetQueryKeys.all, 'list', params] as const,
  listInfinite: (params?: DatasetListParams) =>
    [...datasetQueryKeys.all, 'list-infinite', params] as const,
  details: (datasetId: string) =>
    [...datasetQueryKeys.all, 'details', datasetId] as const,
  versions: (datasetId: string, params?: WithListParams) =>
    [...datasetQueryKeys.all, 'versions', datasetId, params] as const,
  versionFiles: (versionId: string) =>
    [...datasetQueryKeys.all, 'version-files', versionId] as const,
  uploadStatus: (batchId: string) =>
    [...datasetQueryKeys.all, 'upolad-status', batchId] as const,
  annotationCount: (versionId: string) =>
    [...datasetQueryKeys.all, 'annotation-count', versionId] as const
}

export const useGetDatasetsInfinite = (params: DatasetListParams) => {
  return useInfiniteQuery({
    queryKey: datasetQueryKeys.listInfinite(params),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<Dataset[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam: `/v1/datasets/?${objectToQueryString({
      limit: 20,
      ...params
    })}`
  })
}

export const useGetDatasetList = (
  params?: DatasetListParams,
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) => {
  return useQuery({
    queryKey: datasetQueryKeys.list(params),
    queryFn: () => datasetApi.getDatasetList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled
  })
}

export const useGetDatasetDetails = (datasetId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: datasetQueryKeys.details(datasetId),
    queryFn: () => datasetApi.getDatasetDetails(datasetId),
    enabled
  })
}

export const useGetDatasetVersionsList = (
  datasetId: string,
  params?: WithListParams,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: datasetQueryKeys.versions(datasetId, params),
    queryFn: () => datasetApi.getDatasetVersionsList(datasetId, params),
    enabled: options?.enabled
  })
}

type GetDatasetVersionFilesParams = {
  versionId: string
  enabled?: boolean
  listParams?: {
    limit?: number
    ordering?: string
  }
}

export const useGetDatasetVersionFiles = ({
  versionId,
  enabled,
  listParams
}: GetDatasetVersionFilesParams) => {
  return useInfiniteQuery({
    queryKey: datasetQueryKeys.versionFiles(versionId),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<FileMetadata[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam: `/v1/datasets/versions/${versionId}/media/?${objectToQueryString(
      {
        limit: 100,
        ordering: '-created_at',
        ...listParams
      }
    )}`,
    enabled
  })
}

export const useInvalidateFileList = (versionId: string) => {
  const queryClient = useQueryClient()

  const invalidateFileList = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: datasetQueryKeys.versionFiles(versionId)
    })

    void queryClient.invalidateQueries({
      queryKey: datasetQueryKeys.annotationCount(versionId)
    })
  }, [queryClient, versionId])

  return {
    invalidateFileList
  }
}

export const useCreateDataset = () => {
  return useMutation({
    mutationFn: datasetApi.createDataset
  })
}

export const useCreateDatasetVersion = () => {
  return useMutation({
    mutationFn: datasetApi.createDatasetVersion
  })
}

export const useUpdateDataset = () => {
  return useMutation({
    mutationFn: datasetApi.updateDataset
  })
}

export const useDeleteDataset = () => {
  return useMutation({
    mutationFn: datasetApi.deleteDataset
  })
}

export const useDeleteDatasetVersion = () => {
  return useMutation({
    mutationFn: datasetApi.deleteDatasetVersion
  })
}

export const useUploadFilesToDataset = () => {
  return useMutation({
    mutationFn: datasetApi.uploadFilesToDataset
  })
}

export const useGetUploadStatus = (batchId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: datasetQueryKeys.uploadStatus(batchId),
    queryFn: () => datasetApi.getFileUploadStatus(batchId),
    refetchInterval: (query) => {
      const { data, error } = query.state

      const isFinished =
        data?.status === 'Complete' || data?.status === 'Failed' || error

      return isFinished ? false : 3000
    },
    enabled
  })
}

export const useDeleteFiles = () => {
  return useMutation({
    mutationFn: datasetApi.deleteFiles
  })
}

export const useCropDataset = () => {
  return useMutation({
    mutationFn: datasetApi.cropDataset
  })
}

export const useCloneDataset = () => {
  return useMutation({
    mutationFn: datasetApi.cloneDataset
  })
}

export const useGetAnnotationCount = (versionId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: datasetQueryKeys.annotationCount(versionId),
    queryFn: () => datasetApi.getAnnotationCount(versionId),
    enabled
  })
}

// TODO: move other dataset / file queries to this hook
export const useInvalidateDatasetQueries = () => {
  const queryClient = useQueryClient()

  const invalidateDatasetList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...datasetQueryKeys.all, 'list']
    })
  }, [queryClient])

  const invalidateDatasetInfiniteList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...datasetQueryKeys.all, 'list-infinite']
    })
  }, [queryClient])

  const invalidateDatasetDetails = useCallback(
    async (datasetId: string) => {
      await queryClient.invalidateQueries({
        queryKey: datasetQueryKeys.details(datasetId)
      })
    },
    [queryClient]
  )

  const invalidateDatasetVersions = useCallback(
    async (datasetId: string) => {
      await queryClient.invalidateQueries({
        queryKey: [...datasetQueryKeys.all, 'versions', datasetId]
      })
    },
    [queryClient]
  )

  return {
    invalidateDatasetList,
    invalidateDatasetInfiniteList,
    invalidateDatasetDetails,
    invalidateDatasetVersions
  }
}
